import { cloneDeep } from 'lodash';
import { FileMetaData, RunSampleLot, ReportContentItem, Sample } from '../models/run.model';
import { Study } from '../models/study.model';
import { ReportContentSeed } from '../models/report-content-seed.model';
import { getUniqueAccessions } from './helper';

/**
 * Drops run samples whose accession no longer appears in any patient, calibration, or control
 * file metadata. Keeps the Run Setup worklist in sync when a file is removed from Instrument Data.
 */
export function pruneRunSamplesToMetadata(study: Study | null | undefined): Sample[] {
  const runSamples = study?.runSamples;
  if (!runSamples?.length) {
    return runSamples ?? [];
  }

  const validAccessions = new Set(getUniqueAccessions([
    ...(study?.patientFileMetadata || []),
    ...(study?.calFileMetadata || []),
    ...(study?.controlFileMetadata || [])
  ]));

  return runSamples.filter(sample => validAccessions.has((sample.accessionId ?? '').trim().toUpperCase()));
}

/** Matches the id format Run Report Items builds for a row: `${lot}${region}_${runId}`. */
export function buildReportContentItemId(
  lot: string | number | undefined,
  region: number | string | undefined,
  runId: string | undefined
): string {
  return `${lot ?? ''}${parseRegion(region)}_${runId ?? ''}`;
}

/**
 * After an instrument file is removed, identifies Run Report Items rows no longer backed by any
 * remaining instrument data — neither a still-valid seed combo (lot+instrument+assay+region) nor a
 * still-available kit lot (which covers the simpler per-kit-lot placeholder rows, e.g. RMQC).
 */
export function pruneReportContentItemsForRemainingInstrumentData(
  study: Study | null | undefined,
  availableKitLots: string[]
): { reportContentItems: ReportContentItem[]; removedItemIds: string[] } {
  const items = study?.reportContentItems?.reportContentItems ?? [];
  if (!items.length) {
    return { reportContentItems: items, removedItemIds: [] };
  }

  const validSeedIds = new Set(
    buildInstrumentReportSeeds(study).map(seed => buildReportContentItemId(seed.lot, seed.region, seed.runId))
  );
  const validLots = new Set((availableKitLots || []).map(lot => (lot ?? '').toString().trim()));

  const kept: ReportContentItem[] = [];
  const removedItemIds: string[] = [];

  items.forEach(item => {
    const backedBySeed = validSeedIds.has(item.reportContentItemId);
    const backedByLot = validLots.has((item.lot ?? '').toString().trim());
    if (backedBySeed || backedByLot) {
      kept.push(item);
    } else {
      removedItemIds.push(item.reportContentItemId);
    }
  });

  return { reportContentItems: kept, removedItemIds };
}

export function buildInstrumentReportSeeds(study: Study | null | undefined): ReportContentSeed[] {
  if (!study) {
    return [];
  }

  const metadataSources: FileMetaData[] = [
    ...(study.patientFileMetadata || []),
    ...(study.calFileMetadata || []),
    ...(study.controlFileMetadata || [])
  ];

  if (!metadataSources.length) {
    return [];
  }

  const panelKeys = getPanelMatchKeys(study);
  if (!panelKeys.length) {
    return [];
  }

  const allowedAssayNames = getAllowedAssayNames(study);
  const allowedRegions = getAllowedRegions(study);
  const combinations = new Map<string, ReportContentSeed>();
  const seedResultType = getInstrumentSeedResultType(study);

  metadataSources.forEach(file => {
    const reagentLots = (file.reagentLots || [])
      .map(lot => formatReagentLotLabel(String(lot)))
      .filter((lot): lot is string => !!lot);

    if (!reagentLots.length || !file.panels?.length) {
      return;
    }

    const instrumentId = file.instrumentId || '';
    const matchingPanel = file.panels.find(panel =>
      panelKeys.includes(normalizePanelAlias(panel.name))
    );

    if (!matchingPanel?.assays?.length) {
      return;
    }

    matchingPanel.assays.forEach(assay => {
      const region = parseRegion(assay.region);
      const assayName = mapAssayDisplayName(study, assay.name, region);
      if (!assayName || !region) {
        return;
      }
      if (allowedRegions.size && !allowedRegions.has(region)) {
        return;
      }
      if (allowedAssayNames.size && !allowedAssayNames.has(normalizeAssayName(assayName))) {
        return;
      }

      reagentLots.forEach(lot => {
        const key = `${instrumentId}|${lot}|${assayName}|${region}`;
        if (!combinations.has(key)) {
          combinations.set(key, {
            lot,
            runId: instrumentId,
            assay: assayName,
            region,
            resultType: seedResultType
          });
        }
      });
    });
  });

  return Array.from(combinations.values());
}

/**
 * Bead regions carrying the instrument's own QC beads rather than an assay.
 *
 * ISB (34), RBB (98) and SVB (100). They sit on the panel spec sheet like any other region, but
 * they verify the instrument rather than measure a patient, so nothing that reports analytes
 * should list them. Every panel document numbers them the same way, and no panel puts an assay on
 * one of these three regions, so the region alone identifies them.
 *
 * Exported because the Kit C of A filters by exactly this rule. Keeping one definition rather than
 * a copy per consumer is deliberate: the copies had already drifted, and the one missing SVB gave
 * every certificate a blank SVB row.
 */
export const INTERNAL_QC_BEAD_REGIONS: ReadonlySet<number> = new Set<number>([34, 98, 100]);

export function sortReportContentItemsForDisplay(
  items: ReportContentItem[] | null | undefined,
  runType: string | null | undefined
): ReportContentItem[] {
  const list = [...(items ?? [])];
  if (isRmQcRunType(runType)) {
    return list;
  }
  list.sort(compareReportContentItemsForDisplay);
  return list;
}

function isRmQcRunType(runType: string | null | undefined): boolean {
  return (runType ?? '').toUpperCase() === 'RMQC';
}

function compareReportContentItemsForDisplay(a: ReportContentItem, b: ReportContentItem): number {
  // Seeded rows carry a negative sentinel region, so sorting by region alone would lift every
  // unfilled row above the real ones. They trail instead, in the order they were seeded.
  const aSeeded = isSeededPlaceholderRegion(a.region);
  const bSeeded = isSeededPlaceholderRegion(b.region);
  if (aSeeded || bSeeded) {
    return aSeeded === bSeeded ? 0 : (aSeeded ? 1 : -1);
  }

  const aInternal = isInternalQcBeadRegion(a.region);
  const bInternal = isInternalQcBeadRegion(b.region);

  if (aInternal !== bInternal) {
    return aInternal ? 1 : -1;
  }

  const regionDiff = parseRegion(a.region) - parseRegion(b.region);
  if (regionDiff !== 0) {
    return regionDiff;
  }

  return (a.assay ?? '').localeCompare(b.assay ?? '');
}

/** A row seeded by the assay count that nobody has picked a bead region for yet. */
export function isSeededPlaceholderRegion(region: number | string | undefined): boolean {
  return parseRegion(region) < 0;
}

/** Whether a region carries one of the instrument's QC beads rather than an assay. */
export function isInternalQcBeadRegion(region: number | string | undefined): boolean {
  const numericRegion = parseRegion(region);
  return INTERNAL_QC_BEAD_REGIONS.has(numericRegion);
}

function getInstrumentSeedResultType(study: Study): string {
  const runType = (study.runType ?? '').trim().toLowerCase();
  if (runType === 'rmqc' || runType === 'calibrator assignment') {
    return 'Calculated';
  }
  return 'Instrument';
}

function mapAssayDisplayName(study: Study, rawName: string | undefined, region: number): string {
  if (region && !Number.isNaN(region)) {
    const match = study.panelSpecs?.Assay_Specs?.find(spec => Number(spec.bead_region) === region);
    if (match?.assay_name?.trim()) {
      return match.assay_name.trim();
    }
  }

  const normalizedRaw = (rawName ?? '').trim();
  if (!normalizedRaw || normalizedRaw.toLowerCase() === 'nan') {
    return '';
  }

  return normalizedRaw;
}

function getAllowedAssayNames(study: Study): Set<string> {
  const names = new Set<string>();
  (study.panelSpecs?.Assay_Specs ?? []).forEach(spec => {
    const normalized = normalizeAssayName(spec.assay_name);
    if (normalized) {
      names.add(normalized);
    }
  });
  return names;
}

function getAllowedRegions(study: Study): Set<number> {
  const regions = new Set<number>();
  (study.panelSpecs?.Assay_Specs ?? []).forEach(spec => {
    const region = Number(spec.bead_region);
    if (Number.isFinite(region) && region > 0) {
      regions.add(region);
    }
  });
  return regions;
}

function normalizeAssayName(value?: string | null): string {
  if (!value) {
    return '';
  }
  return value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

const PANEL_ALIAS_MAP: Record<string, string> = {
  'apls-g': 'apls igg',
  'aplsg': 'apls igg',
  'vasculitis': 'vask',
  'vasc': 'vask',
  'vask': 'vask',
  'mmrv-g': 'mmrv igg',
  'mmrvg': 'mmrv igg',
  'lymet': 'lyme total',
  'lyme-t': 'lyme total',
  'ebv-m': 'ebv igm',
  'ebvm': 'ebv igm',
  'hsv-g': 'hsv igg',
  'hsvg': 'hsv igg',
  'ebv-g': 'ebv igg',
  'ebvg': 'ebv igg',
  'apls-m': 'apls igm',
  'aplsm': 'apls igm',
  'apls-a': 'apls iga',
  'aplsa': 'apls iga',
  'torc-g': 'torc igg',
  'torcg': 'torc igg',
  'ccp': 'anti-ccp',
  'celi-a': 'celiac iga',
  'celia': 'celiac iga',
  'celi-g': 'celiac igg',
  'celig': 'celiac igg'
};

function normalizePanelAlias(value?: string): string {
  const normalized = normalizePanelName(value);
  if (!normalized) {
    return '';
  }
  return PANEL_ALIAS_MAP[normalized] ?? normalized;
}

function getPanelMatchKeys(study: Study): string[] {
  const keys = new Set<string>();
  const addKey = (value?: string) => {
    if (!value) {
      return;
    }

    const normalized = normalizePanelAlias(value);
    if (normalized) {
      keys.add(normalized);
    }
  };

  addKey(study.panelSpecs?.kitname);
  addKey(study.panelSpecs?.kitname?.split(' ')[0]);

  return Array.from(keys);
}

function normalizePanelName(value?: string): string {
  return (value ?? '').trim().toLowerCase();
}

function parseRegion(region: string | number | undefined): number {
  if (typeof region === 'number') {
    return Number.isNaN(region) ? 0 : region;
  }

  const parsed = Number(region);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatReagentLotLabel(lot: string): string {
  if (lot === undefined || lot === null) {
    return '';
  }

  const numeric = Number(lot);
  if (!Number.isNaN(numeric)) {
    return Math.trunc(numeric).toString();
  }

  return lot.trim();
}

/**
 * Applies each sample's `sampleTypeOverrides` (keyed by reportContentItemId) to the run-sample lots,
 * so the Run Samples tab shows the override in that report item's column.
 * - Lots are rebuilt per report item with `runSamples` in the same order as `samples`; cells with no
 *   override take the sample's own type.
 * - Existing lots are kept as the base so their mean type survives; keys matching no report item are ignored.
 * - Returns the source lots untouched when no sample carries an override.
 */
export function applySampleTypeOverrides(
  sourceLots: RunSampleLot[] | undefined,
  samples: Sample[] | undefined,
  items: { reportContentItemId: string | number | undefined }[]
): RunSampleLot[] {
  const hasOverrides = (samples ?? []).some(s => Object.keys(s?.sampleTypeOverrides ?? {}).length > 0);
  if (!hasOverrides) {
    return sourceLots ?? [];
  }

  return alignRunSampleLots(sourceLots, items).map(lot => {
    const key = String(lot.reportContentItemId ?? '');
    lot.runSamples = (samples ?? []).map(sample => ({
      sampleId: sample.sampleId as string,
      sampleType: sample.sampleTypeOverrides?.[key] || sample.sampleType || sample.defaultSampleType
    }));
    return lot;
  });
}

/**
 * Aligns the run-sample lot array with the latest report-content rows.
 * - Existing entries (matched by reportContentItemId) are cloned so their mean/sample selections survive.
 * - Newly added rows get blank RunSampleLot shells so the UI can edit them immediately.
 */
export function alignRunSampleLots(
  sourceLots: RunSampleLot[] | undefined,
  items: { reportContentItemId: string | number | undefined }[]
): RunSampleLot[] {
  const lotMap = new Map<string, RunSampleLot>();
  (sourceLots ?? []).forEach(lot => {
    const key = String(lot?.reportContentItemId ?? '');
    lotMap.set(key, cloneDeep(lot));
  });

  return (items ?? []).map(item => {
    const id = item.reportContentItemId ?? '';
    const key = String(id);
    const existing = lotMap.get(key);
    if (existing) {
      existing.reportContentItemId = id as number;
      return existing;
    }
    const blank = new RunSampleLot();
    blank.reportContentItemId = id as number;
    blank.meanType = '-';
    blank.runSamples = [];
    return blank;
  });
}
